import {
  Box, Breadcrumbs, Drawer, IconButton, Link, Tooltip, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { ReactNode } from 'react'
import { useDrillStack } from './useDrillStack'
import {
  DrillExportProvider, useDrillExportContext,
} from './DrillExportContext'
import { useExportExcel } from 'features/executive/export/use-export-excel'
import type { ExecutiveState } from 'app/slice/executive.slice'

interface Props {
  children?: ReactNode
}

function DrillExportButton() {
  const { t } = useTranslation()
  const { payload, widgetKey, widgetTitle } = useDrillExportContext()
  const { exportDrill } = useExportExcel()
  const isLoading = useSelector((s: { executive: ExecutiveState }) =>
    widgetKey ? !!s.executive.loadingWidgets[widgetKey] : false
  )

  const disabled = !payload || !widgetTitle || isLoading
  const tooltip = disabled
    ? t('EXECUTIVE.EXPORT.DISABLED_LOADING')
    : t('EXECUTIVE.EXPORT.EXCEL_BUTTON')

  return (
    <Tooltip title={tooltip}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          aria-label="export excel"
          onClick={() => {
            if (payload && widgetTitle) exportDrill(payload, widgetTitle)
          }}
        >
          <DownloadIcon />
        </IconButton>
      </span>
    </Tooltip>
  )
}

export default function DrillDrawer({ children }: Props) {
  const { stack, clear } = useDrillStack()
  const open = stack.length > 0

  return (
    <DrillExportProvider>
      <Drawer
        anchor="right"
        open={open}
        onClose={clear}
        PaperProps={{
          sx: { width: { xs: '100%', md: '60%' }, minWidth: { md: 720 }, maxWidth: 1280 },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Breadcrumbs separator="›">
            {stack.map((lvl, i) => (
              <Link
                key={lvl.id}
                underline={i === stack.length - 1 ? 'none' : 'hover'}
                color={i === stack.length - 1 ? 'text.primary' : 'inherit'}
                component="button"
                onClick={() => { /* Phase 1G: pop stack */ }}
              >
                {lvl.label}
              </Link>
            ))}
          </Breadcrumbs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DrillExportButton />
            <IconButton size="small" onClick={clear} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: 3 }}>
          {children ?? (
            <Typography variant="body2" color="text.secondary">
              เนื้อหา drill-down จะพัฒนาในเฟส 1E
            </Typography>
          )}
        </Box>
      </Drawer>
    </DrillExportProvider>
  )
}
